import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem,
  FormControl, InputLabel, Select, Alert, Chip, Grid,
  IconButton, Tooltip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, LinearProgress, Divider, Collapse,
  InputAdornment, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Warning, Error as ErrorIcon, Info, CheckCircle, Refresh, Add,
  WaterDrop, Cloud, Memory, Person,
  Schedule, FilterList, Close, Send,
  Notifications, ExpandMore, ExpandLess,
  TableChart, ViewTimeline, ArrowUpward,
  Shield, LocalOffer,
  CrisisAlert, Policy, Gavel, Agriculture,
  Search,
} from '@mui/icons-material';
import { alertsAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const SEVERITY_CONF = {
  critical: { label: 'Critical', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: <CrisisAlert sx={{ fontSize: 14 }} /> },
  warning:  { label: 'Warning',  color: '#d97706', bg: '#fef3c7', border: '#fcd34d', icon: <Warning sx={{ fontSize: 14 }} /> },
  info:     { label: 'Info',     color: '#2563eb', bg: '#dbeafe', border: '#93c5fd', icon: <Info sx={{ fontSize: 14 }} /> },
};

const SOURCE_CONF = {
  manual:    { label: 'Manual',    color: '#64748b', icon: <Person sx={{ fontSize: 14 }} /> },
  weather:   { label: 'Weather',   color: '#0891b2', icon: <Cloud sx={{ fontSize: 14 }} /> },
  ai:        { label: 'AI',        color: '#7c3aed', icon: <Memory sx={{ fontSize: 14 }} /> },
  iot:       { label: 'IoT',       color: '#059669', icon: <Memory sx={{ fontSize: 14 }} /> },
  system:    { label: 'System',    color: '#475569', icon: <Info sx={{ fontSize: 14 }} /> },
  scheduler: { label: 'Scheduler', color: '#d97706', icon: <Schedule sx={{ fontSize: 14 }} /> },
};

const ALERT_TYPES = [
  { value: 'doc_reminder',    label: 'DOC Reminder' },
  { value: 'weather_warning', label: 'Weather Warning' },
  { value: 'disease_alert',   label: 'Disease Alert' },
  { value: 'feed_reminder',   label: 'Feed Reminder' },
  { value: 'policy_expiry',   label: 'Policy Expiry' },
  { value: 'claim_update',    label: 'Claim Update' },
  { value: 'water_quality',   label: 'Water Quality' },
  { value: 'mortality_alert', label: 'Mortality Alert' },
  { value: 'compliance',      label: 'Compliance' },
  { value: 'general',         label: 'General' },
];

const ALERT_TYPE_LABEL = Object.fromEntries(ALERT_TYPES.map(t => [t.value, t.label]));

const IMPACT_SCOPE = {
  mortality_alert: 'Stock Loss',
  disease_alert:   'Biosecurity',
  water_quality:   'Environment',
  weather_warning: 'Operations',
  policy_expiry:   'Coverage Gap',
  claim_update:    'Financial',
  compliance:      'Regulatory',
  feed_reminder:   'Nutrition',
  doc_reminder:    'Growth Cycle',
  general:         'General',
};

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Compute human-readable age of an alert */
const alertAge = (createdAt) => {
  if (!createdAt) return '—';
  const ms = Date.now() - new Date(createdAt).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

// ═══════════════════════════════════════════════════════════════
// KPI CARD
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, onClick, active }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 2, cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid #e2e8f0',
      transition: 'all 0.2s ease',
      background: active ? `${color}08` : '#fff',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20`, borderColor: color } : {},
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// SEVERITY DISTRIBUTION BAR
// ═══════════════════════════════════════════════════════════════

const SeverityBar = ({ alerts }) => {
  const total = alerts.length;
  if (total === 0) return null;

  const critical = alerts.filter(a => a.severity === 'critical').length;
  const warning  = alerts.filter(a => a.severity === 'warning').length;
  const info     = alerts.filter(a => a.severity === 'info').length;

  const segments = [
    { count: critical, ...SEVERITY_CONF.critical, key: 'critical' },
    { count: warning,  ...SEVERITY_CONF.warning,  key: 'warning' },
    { count: info,     ...SEVERITY_CONF.info,     key: 'info' },
  ].filter(s => s.count > 0);

  return (
    <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box className="flex items-center justify-between mb-1">
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Severity Distribution
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
            {total} total alerts
          </Typography>
        </Box>

        {/* Bar */}
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
          {segments.map(seg => (
            <Tooltip key={seg.key} title={`${seg.label}: ${seg.count} (${((seg.count / total) * 100).toFixed(0)}%)`}>
              <Box sx={{ width: `${(seg.count / total) * 100}%`, bgcolor: seg.color, transition: 'width 0.3s ease' }} />
            </Tooltip>
          ))}
        </Box>

        {/* Legend */}
        <Box className="flex items-center gap-3 mt-1">
          {segments.map(seg => (
            <Box key={seg.key} className="flex items-center gap-1">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: seg.color }} />
              <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 600 }}>
                {seg.label}: {seg.count}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════
// TIMELINE VIEW
// ═══════════════════════════════════════════════════════════════

const TimelineView = ({ alerts, onSelect, selectedId }) => {
  if (alerts.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ py: 8, color: '#cbd5e1' }}>
        <Notifications sx={{ fontSize: 48, opacity: 0.2, mb: 1.5 }} />
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#94a3b8' }}>
          No incidents to display
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.5 }}>
          All alerts have been resolved or no alerts match the current filters.
        </Typography>
      </Box>
    );
  }

  // Group by date
  const grouped = {};
  alerts.forEach(a => {
    const dateKey = fmtDate(a.created_at);
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(a);
  });

  return (
    <Box sx={{ p: 2 }}>
      {Object.entries(grouped).map(([dateStr, dayAlerts]) => (
        <Box key={dateStr} sx={{ mb: 3 }}>
          <Box className="flex items-center gap-2 mb-1.5">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb' }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              {dateStr}
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: '#e2e8f0' }} />
            <Chip label={`${dayAlerts.length}`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Box>

          {dayAlerts.map((alert) => {
            const sev = SEVERITY_CONF[alert.severity] || SEVERITY_CONF.info;
            const src = SOURCE_CONF[alert.source] || SOURCE_CONF.system;
            const isActive = selectedId === alert.id;

            return (
              <Box
                key={alert.id}
                onClick={() => onSelect(alert)}
                sx={{
                  ml: 2, pl: 2, py: 1.2, cursor: 'pointer',
                  borderLeft: `3px solid ${isActive ? '#2563eb' : sev.border}`,
                  bgcolor: isActive ? '#eff6ff' : 'transparent',
                  borderRadius: '0 8px 8px 0',
                  transition: 'all 0.15s ease',
                  mb: 0.5,
                  '&:hover': { bgcolor: isActive ? '#eff6ff' : '#f8fafc' },
                }}
              >
                <Box className="flex items-start gap-2">
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: sev.bg, flexShrink: 0, mt: 0.2 }}>
                    {sev.icon}
                  </Box>
                  <Box className="flex-1 min-w-0">
                    <Box className="flex items-center gap-1.5">
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {alert.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', flexShrink: 0 }}>
                        {alertAge(alert.created_at)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {alert.message}
                    </Typography>
                    <Box className="flex items-center gap-1 mt-0.5 flex-wrap">
                      <Chip label={sev.label} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: sev.bg, color: sev.color }} />
                      <Chip label={src.label} size="small" icon={src.icon}
                        sx={{ height: 16, fontSize: '0.55rem', fontWeight: 600, bgcolor: '#f1f5f9', color: src.color, '& .MuiChip-icon': { color: src.color, fontSize: 12 } }} />
                      {!alert.acknowledged && (
                        <Chip label="Unresolved" size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// DETAIL PANEL (right side)
// ═══════════════════════════════════════════════════════════════

const DetailPanel = ({ alert, onClose }) => {
  if (!alert) {
    return (
      <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1', px: 2 }}>
        <Shield sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>
          Select an alert to view details
        </Typography>
      </Box>
    );
  }

  const sev = SEVERITY_CONF[alert.severity] || SEVERITY_CONF.info;
  const src = SOURCE_CONF[alert.source] || SOURCE_CONF.system;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: sev.bg, borderBottom: `2px solid ${sev.border}`, flexShrink: 0 }}>
        <Box className="flex items-start justify-between">
          <Box className="flex items-center gap-1.5 min-w-0 flex-1">
            {sev.icon}
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: sev.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {alert.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8', flexShrink: 0 }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Full Context */}
        <Section icon={<Info sx={{ fontSize: 14 }} />} title="Full Context" color="#2563eb">
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#1e293b', lineHeight: 1.6 }}>
                {alert.message}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box className="grid grid-cols-2 gap-y-1">
                <InfoRow label="Alert ID" value={`#${alert.id}`} />
                <InfoRow label="Type" value={ALERT_TYPE_LABEL[alert.alert_type] || alert.alert_type} />
                <InfoRow label="Severity" value={
                  <Chip label={sev.label} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: sev.bg, color: sev.color }} />
                } />
                <InfoRow label="Source" value={
                  <Chip label={src.label} size="small" icon={src.icon}
                    sx={{ height: 16, fontSize: '0.55rem', fontWeight: 600, bgcolor: '#f1f5f9', color: src.color, '& .MuiChip-icon': { color: src.color, fontSize: 12 } }} />
                } />
                <InfoRow label="Impact" value={IMPACT_SCOPE[alert.alert_type] || 'General'} />
                <InfoRow label="Age" value={alertAge(alert.created_at)} />
                <InfoRow label="Created" value={fmtDateTime(alert.created_at)} />
                <InfoRow label="Curated" value={alert.is_curated ? 'Visible' : 'Internal'} />
              </Box>
            </CardContent>
          </Card>
        </Section>

        {/* Related Farmer */}
        <Section icon={<Agriculture sx={{ fontSize: 14 }} />} title="Related Farmer" color="#16a34a">
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {alert.farmer_id ? (
                <Box className="flex items-center gap-2">
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Person sx={{ fontSize: 16, color: '#16a34a' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
                      Farmer #{alert.farmer_id}
                    </Typography>
                    {alert.pond_name && (
                      <Box className="flex items-center gap-0.5 mt-0.3">
                        <WaterDrop sx={{ fontSize: 12, color: '#0891b2' }} />
                        <Typography sx={{ fontSize: '0.65rem', color: '#0891b2', fontWeight: 600 }}>
                          {alert.pond_name}
                        </Typography>
                      </Box>
                    )}
                    {alert.pond_id && !alert.pond_name && (
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                        Pond #{alert.pond_id}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  No specific farmer linked — broadcast alert
                </Typography>
              )}
            </CardContent>
          </Card>
        </Section>

        {/* Related Policy */}
        <Section icon={<Policy sx={{ fontSize: 14 }} />} title="Related Policy" color="#7c3aed">
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {alert.policy_id ? (
                <Box className="flex items-center gap-2">
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Policy sx={{ fontSize: 14, color: '#7c3aed' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
                    Policy #{alert.policy_id}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  No policy linked to this alert
                </Typography>
              )}
            </CardContent>
          </Card>
        </Section>

        {/* Related Claim */}
        <Section icon={<Gavel sx={{ fontSize: 14 }} />} title="Related Claim" color="#dc2626">
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {alert.claim_id ? (
                <Box className="flex items-center gap-2">
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Gavel sx={{ fontSize: 14, color: '#dc2626' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
                    Claim #{alert.claim_id}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  No claim linked to this alert
                </Typography>
              )}
            </CardContent>
          </Card>
        </Section>

        {/* Action History */}
        <Section icon={<ViewTimeline sx={{ fontSize: 14 }} />} title="Action History" color="#d97706">
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {/* Timeline entries */}
              <TimelineEntry color="#2563eb" label="Alert Created" sub={fmtDateTime(alert.created_at)} icon={<Notifications sx={{ fontSize: 12 }} />} />
              {alert.action_required && (
                <TimelineEntry color="#d97706" label="Action Required" sub="Farmer must take action" icon={<Warning sx={{ fontSize: 12 }} />} />
              )}
              {alert.acknowledged ? (
                <TimelineEntry color="#16a34a" label="Acknowledged / Resolved" sub={alert.acknowledged_at ? fmtDateTime(alert.acknowledged_at) : 'Timestamp unavailable'} icon={<CheckCircle sx={{ fontSize: 12 }} />} last />
              ) : (
                <TimelineEntry color="#dc2626" label="Pending Resolution" sub="Awaiting acknowledgement" icon={<Schedule sx={{ fontSize: 12 }} />} last />
              )}
            </CardContent>
          </Card>
        </Section>
      </Box>

      {/* Tags Footer */}
      <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e2e8f0', flexShrink: 0, bgcolor: '#f8fafc' }}>
        <Box className="flex items-center gap-1 mb-0.5">
          <LocalOffer sx={{ fontSize: 12, color: '#94a3b8' }} />
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Tags</Typography>
        </Box>
        <Box className="flex flex-wrap gap-1">
          <Chip label={sev.label} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: sev.bg, color: sev.color }} />
          <Chip label={src.label} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: '#f1f5f9', color: src.color }} />
          <Chip label={ALERT_TYPE_LABEL[alert.alert_type] || alert.alert_type} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
          {alert.action_required && <Chip label="Action Req." size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#d97706' }} />}
          {!alert.acknowledged && <Chip label="Unresolved" size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />}
        </Box>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// SMALL HELPERS
// ═══════════════════════════════════════════════════════════════

const Section = ({ icon, title, color, children }) => (
  <Box>
    <Box className="flex items-center gap-1 mb-1">
      {React.cloneElement(icon, { sx: { fontSize: 14, color } })}
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

const TimelineEntry = ({ color, label, sub, icon, last }) => (
  <Box className="flex items-start gap-2" sx={{ mb: last ? 0 : 1.5 }}>
    <Box className="flex flex-col items-center" sx={{ minWidth: 14 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: 0.3 }} />
      {!last && <Box sx={{ width: 1, flex: 1, bgcolor: '#e2e8f0', mt: 0.5, minHeight: 16 }} />}
    </Box>
    <Box className="min-w-0 flex-1">
      <Box className="flex items-center gap-1">
        {icon}
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{sub}</Typography>
    </Box>
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const AlertsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // View toggle
  const [viewMode, setViewMode] = useState('table');

  // Create alert dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    alert_type: 'general', severity: 'info', source: 'manual',
    title: '', message: '', farmer_id: '',
    action_required: false, is_curated: true,
  });

  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load ──
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);

      const [alertsRes, statsRes] = await Promise.all([
        alertsAPI.admin.getAll({ limit: 500 }),
        alertsAPI.admin.getStats(),
      ]);

      setAlerts(alertsRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setSnackbar({ open: true, message: 'Failed to load alerts', severity: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Computed Stats ──
  const kpiStats = useMemo(() => {
    const total = alerts.length;
    const critical = alerts.filter(a => a.severity === 'critical').length;
    const unresolved = alerts.filter(a => !a.acknowledged).length;
    const escalated = alerts.filter(a => a.action_required && !a.acknowledged).length;
    const autoResolved = alerts.filter(a => a.acknowledged && a.source !== 'manual').length;
    return { total, critical, unresolved, escalated, autoResolved };
  }, [alerts]);

  // ── Filter ──
  const filteredAlerts = useMemo(() => {
    return alerts.filter(a => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const match = (a.title || '').toLowerCase().includes(q)
          || (a.message || '').toLowerCase().includes(q)
          || String(a.id).includes(q)
          || (a.pond_name || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (severityFilter !== 'all' && a.severity !== severityFilter) return false;
      if (sourceFilter !== 'all' && a.source !== sourceFilter) return false;
      if (typeFilter !== 'all' && a.alert_type !== typeFilter) return false;
      return true;
    });
  }, [alerts, searchTerm, severityFilter, sourceFilter, typeFilter]);

  // ── Create Alert ──
  const handleCreateAlert = async () => {
    try {
      setSubmitting(true);
      if (!formData.title.trim() || !formData.message.trim()) {
        setSnackbar({ open: true, message: 'Title and message are required', severity: 'error' });
        return;
      }

      await alertsAPI.admin.create({
        alert_type: formData.alert_type,
        severity: formData.severity,
        source: formData.source,
        title: formData.title.trim(),
        message: formData.message.trim(),
        farmer_id: formData.farmer_id ? parseInt(formData.farmer_id) : null,
        action_required: formData.action_required,
        is_curated: formData.is_curated,
      });

      setSnackbar({ open: true, message: 'Alert created successfully', severity: 'success' });
      setCreateDialogOpen(false);
      setFormData({ alert_type: 'general', severity: 'info', source: 'manual', title: '', message: '', farmer_id: '', action_required: false, is_curated: true });
      loadData(true);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to create alert', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Escalation status derivation ──
  const getEscalationStatus = (alert) => {
    if (alert.acknowledged) return { label: 'Resolved', color: '#16a34a', bg: '#dcfce7' };
    if (alert.severity === 'critical' && alert.action_required) return { label: 'Escalated', color: '#dc2626', bg: '#fef2f2' };
    if (alert.action_required) return { label: 'Action Req.', color: '#d97706', bg: '#fef3c7' };
    return { label: 'Open', color: '#2563eb', bg: '#dbeafe' };
  };

  // ── Loading State ──
  if (loading && alerts.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading incident data…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ 1. KPI STRIP ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-3">
        <KPICard icon={<Notifications />} label="Total Alerts" value={kpiStats.total} color="#2563eb"
          onClick={() => { setSeverityFilter('all'); setSourceFilter('all'); setTypeFilter('all'); }}
          active={severityFilter === 'all' && sourceFilter === 'all' && typeFilter === 'all'} />
        <KPICard icon={<CrisisAlert />} label="Critical" value={kpiStats.critical} color="#dc2626"
          onClick={() => { setSeverityFilter('critical'); setSourceFilter('all'); setTypeFilter('all'); }}
          active={severityFilter === 'critical'} />
        <KPICard icon={<ErrorIcon />} label="Unresolved" value={kpiStats.unresolved} color="#d97706" />
        <KPICard icon={<ArrowUpward />} label="Escalated" value={kpiStats.escalated} color="#9333ea" />
        <KPICard icon={<CheckCircle />} label="Auto-Resolved" value={kpiStats.autoResolved} color="#16a34a" />
      </Box>

      {/* ══════════ 2. SEVERITY BAR ══════════ */}
      <Box sx={{ mb: 2 }}>
        <SeverityBar alerts={alerts} />
      </Box>

      {/* ══════════ 3. FILTER + VIEW TOGGLE ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box className="flex flex-wrap items-center gap-2">
            <TextField
              size="small" placeholder="Search alerts…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            />
            <Button size="small" variant={showFilters ? 'contained' : 'outlined'}
              onClick={() => setShowFilters(!showFilters)} startIcon={<FilterList />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Filters {showFilters ? <ExpandLess sx={{ ml: 0.5 }} /> : <ExpandMore sx={{ ml: 0.5 }} />}
            </Button>

            <Box className="flex-1" />

            {/* 3️⃣ View toggle */}
            <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(e, v) => { if (v) setViewMode(v); }}
              sx={{ '& .MuiToggleButton-root': { borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5 } }}>
              <ToggleButton value="table"><TableChart sx={{ fontSize: 16, mr: 0.5 }} /> Table</ToggleButton>
              <ToggleButton value="timeline"><ViewTimeline sx={{ fontSize: 16, mr: 0.5 }} /> Timeline</ToggleButton>
            </ToggleButtonGroup>

            <Button size="small" variant="contained" startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>
              New Alert
            </Button>
            <Tooltip title="Refresh"><IconButton size="small" onClick={() => loadData(true)} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
            <Chip label={`${filteredAlerts.length} of ${alerts.length}`} size="small" sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Box>

          <Collapse in={showFilters}>
            <Box className="flex flex-wrap items-center gap-2 mt-2 pt-2" sx={{ borderTop: '1px solid #e2e8f0' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Severity</InputLabel>
                <Select value={severityFilter} label="Severity" onChange={(e) => setSeverityFilter(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Source</InputLabel>
                <Select value={sourceFilter} label="Source" onChange={(e) => setSourceFilter(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All</MenuItem>
                  {Object.entries(SOURCE_CONF).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Types</MenuItem>
                  {ALERT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
              {(severityFilter !== 'all' || sourceFilter !== 'all' || typeFilter !== 'all' || searchTerm) && (
                <Button size="small" onClick={() => { setSeverityFilter('all'); setSourceFilter('all'); setTypeFilter('all'); setSearchTerm(''); }}
                  sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600 }}>Clear All</Button>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ══════════ 4. MAIN: TABLE/TIMELINE + DETAIL PANEL ══════════ */}
      <Box className="flex gap-2.5" sx={{ height: 'calc(100vh - 370px)', minHeight: 420 }}>

        {/* Left: Table or Timeline */}
        <Card sx={{ flex: 1, borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#0f172a', flexShrink: 0 }}>
            <Box className="flex items-center justify-between">
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {viewMode === 'table' ? 'Incident Table' : 'Incident Timeline'} ({filteredAlerts.length})
              </Typography>
              {stats && (
                <Typography sx={{ fontSize: '0.6rem', color: '#64748b' }}>
                  Last 7 days: {stats.last_7_days || 0}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {viewMode === 'table' ? (
              filteredAlerts.length === 0 ? (
                <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1' }}>
                  <Notifications sx={{ fontSize: 48, opacity: 0.2, mb: 1.5 }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#94a3b8' }}>
                    No incidents to display
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.5 }}>
                    {severityFilter !== 'all' || sourceFilter !== 'all' || typeFilter !== 'all'
                      ? 'Try adjusting the filters to see more results.'
                      : 'No alerts have been generated yet. The system is running smoothly.'}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Alert</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Type</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Severity</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Age</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Impact</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Escalation</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Source</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.68rem', color: '#64748b' }}>Target</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAlerts.map((alert) => {
                        const sev = SEVERITY_CONF[alert.severity] || SEVERITY_CONF.info;
                        const src = SOURCE_CONF[alert.source] || SOURCE_CONF.system;
                        const esc = getEscalationStatus(alert);
                        const isActive = selectedAlert?.id === alert.id;

                        return (
                          <TableRow key={alert.id} hover onClick={() => setSelectedAlert(alert)}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: isActive ? '#eff6ff' : (!alert.acknowledged ? `${sev.bg}80` : 'transparent'),
                              borderLeft: isActive ? '3px solid #2563eb' : '3px solid transparent',
                              '&:hover': { bgcolor: isActive ? '#eff6ff' : '#f8fafc' },
                            }}>
                            <TableCell>
                              <Box sx={{ maxWidth: 220 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {alert.title}
                                </Typography>
                                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {alert.message}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={ALERT_TYPE_LABEL[alert.alert_type] || alert.alert_type} size="small"
                                sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
                            </TableCell>
                            <TableCell>
                              <Chip label={sev.label} size="small" icon={sev.icon}
                                sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: sev.bg, color: sev.color, '& .MuiChip-icon': { color: sev.color, fontSize: 12 } }} />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b' }}>
                                {alertAge(alert.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#475569' }}>
                                {IMPACT_SCOPE[alert.alert_type] || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={esc.label} size="small"
                                sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: esc.bg, color: esc.color }} />
                            </TableCell>
                            <TableCell>
                              <Box className="flex items-center gap-0.5">
                                {src.icon}
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: src.color }}>{src.label}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {alert.farmer_id ? (
                                <Box>
                                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#1e293b' }}>Farmer #{alert.farmer_id}</Typography>
                                  {alert.pond_name && (
                                    <Box className="flex items-center gap-0.5">
                                      <WaterDrop sx={{ fontSize: 10, color: '#0891b2' }} />
                                      <Typography sx={{ fontSize: '0.58rem', color: '#0891b2' }}>{alert.pond_name}</Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>All</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            ) : (
              <TimelineView alerts={filteredAlerts} onSelect={setSelectedAlert} selectedId={selectedAlert?.id} />
            )}
          </Box>
        </Card>

        {/* Right: Detail Panel */}
        <Card sx={{ flex: '0 0 310px', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <DetailPanel alert={selectedAlert} onClose={() => setSelectedAlert(null)} />
        </Card>
      </Box>

      {/* ══════════ CREATE ALERT DIALOG ══════════ */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#0f172a', color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
          <Box className="flex items-center gap-1.5"><Add sx={{ fontSize: 18 }} /> Create New Alert</Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Alert Type</InputLabel>
                <Select value={formData.alert_type} label="Alert Type" onChange={(e) => setFormData({ ...formData, alert_type: e.target.value })} sx={{ borderRadius: 2 }}>
                  {ALERT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select value={formData.severity} label="Severity" onChange={(e) => setFormData({ ...formData, severity: e.target.value })} sx={{ borderRadius: 2 }}>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Title" value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={3} label="Message" value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Farmer ID (Optional)" value={formData.farmer_id}
                onChange={(e) => setFormData({ ...formData, farmer_id: e.target.value })}
                helperText="Leave empty to broadcast" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select value={formData.is_curated} label="Visibility" onChange={(e) => setFormData({ ...formData, is_curated: e.target.value })} sx={{ borderRadius: 2 }}>
                  <MenuItem value={true}>Visible to Farmers</MenuItem>
                  <MenuItem value={false}>Internal Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<Send />} onClick={handleCreateAlert} disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>
            Create Alert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertsManagement;
